// src/features/scheduleGenerator/components/TaskAssignmentModal.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Check } from 'lucide-react';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { TaskRecommendation, TaskAssignmentTimeBox } from '../services/scheduleGeneratorService';

interface RecommendedTask {
  id: string;
  title: string;
  category?: string;
  estimatedTime?: number;
  reason?: string;
}

interface TaskAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Phase: recommend (勾選推薦清單) or assign (確認配對結果)
  phase: 'recommend' | 'assign';
  isLoading: boolean;
  isApplying?: boolean;
  // recommend phase
  recommendations: TaskRecommendation[];
  availableTasks: RecommendedTask[];
  // assign phase
  assignedTimeBoxes: TaskAssignmentTimeBox[];
  onBack: () => void;
  onConfirmSelection: (selectedTaskIds: string[]) => void;
  onApply: () => void;
  error: string | null;
}

const TaskAssignmentModal: React.FC<TaskAssignmentModalProps> = ({
  isOpen,
  onClose,
  phase,
  isLoading,
  isApplying = false,
  recommendations,
  availableTasks,
  assignedTimeBoxes,
  onBack,
  onConfirmSelection,
  onApply,
  error,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(recommendations.map(r => r.taskId))
  );
  // local removals in assign phase
  const [removedAssignments, setRemovedAssignments] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(recommendations.map(r => r.taskId)));
      setRemovedAssignments(new Set());
    }
  }, [isOpen]); // recommendations 不放 deps：只在 open 時初始化一次，避免外部資料更新覆蓋用戶選擇

  const reasonMap = useMemo(() =>
    Object.fromEntries(recommendations.map(r => [r.taskId, r.reason])),
    [recommendations]
  );

  const toggleTask = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const removeAssignment = (taskId: string) => {
    setRemovedAssignments(prev => new Set(Array.from(prev).concat(taskId)));
  };

  const filteredTimeBoxes = useMemo(() =>
    assignedTimeBoxes.map(tb => ({
      ...tb,
      taskIds: (tb.taskIds || []).filter(tid => !removedAssignments.has(tid)),
    })),
    [assignedTimeBoxes, removedAssignments]
  );

  const taskLookup = useMemo(() =>
    Object.fromEntries(availableTasks.map(t => [t.id, t])),
    [availableTasks]
  );

  // 手機：底部滑入；桌機：輕微下移 + fade
  const cardVariants = isDesktop
    ? {
        initial: { opacity: 0, y: -8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
      };

  const cardTransition = isDesktop
    ? ({ duration: 0.2, ease: 'easeOut' } as const)
    : ({ type: 'spring', damping: 30, stiffness: 300 } as const);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-modal-nested flex justify-center items-end sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label={phase === 'recommend' ? 'Select tasks for today' : 'Confirm task assignments'}
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-neutral-900 bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className="relative bg-white rounded-t-xl md:rounded-lg w-full sm:max-w-md sm:mx-auto shadow-xl flex flex-col max-h-[85vh]"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={cardTransition}
          >
            {/* drag handle */}
            <div className="sm:hidden w-full flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-neutral-300 rounded-full" />
            </div>

            {/* header */}
            <div className="flex justify-between items-center px-4 pt-3 pb-3 shrink-0">
              <h2 className="text-lg font-semibold text-neutral-800">
                {phase === 'recommend' ? 'Suggested Tasks' : 'Task Assignments'}
              </h2>
              <button
                onClick={onClose}
                disabled={isApplying}
                className="text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {error && (
              <p className="px-4 pb-2 text-sm text-red-500 shrink-0">{error}</p>
            )}

            {/* content */}
            <div className="overflow-y-auto flex-1 border-t border-neutral-100">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <svg className="animate-spin h-5 w-5 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm text-neutral-400">
                    {isApplying
                      ? 'Applying schedule…'
                      : phase === 'recommend'
                      ? 'Finding tasks…'
                      : 'Matching tasks to blocks…'}
                  </span>
                </div>
              ) : phase === 'recommend' ? (
                availableTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-1 px-6 text-center">
                    <p className="text-sm text-neutral-500">No tasks to suggest.</p>
                    <p className="text-xs text-neutral-400">Add tasks first, then come back.</p>
                  </div>
                ) : (
                  availableTasks.map(task => {
                    const isSelected = selectedIds.has(task.id);
                    const reason = reasonMap[task.id];
                    return (
                      <button
                        key={task.id}
                        type="button"
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={task.title}
                        onClick={() => toggleTask(task.id)}
                        className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-neutral-100 last:border-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${
                          isSelected ? 'bg-sky-50' : 'hover:bg-neutral-50'
                        }`}
                      >
                        <span className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-sky-500 border-sky-500' : 'border-neutral-300'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-800 truncate">{task.title}</p>
                          {reason && <p className="text-xs text-neutral-400 mt-0.5">{reason}</p>}
                        </div>
                      </button>
                    );
                  })
                )
              ) : (
                // assign phase: TimeBox 展開式
                filteredTimeBoxes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="text-sm text-neutral-500">No assignments.</p>
                  </div>
                ) : (
                  filteredTimeBoxes.map(tb => (
                    <div key={tb.id} className="border-b border-neutral-100 last:border-0">
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-neutral-50">
                        <span className="text-xs text-neutral-400 font-mono shrink-0">
                          {tb.startTime} – {tb.endTime}
                        </span>
                        <span className="text-sm font-semibold text-neutral-800 truncate">{tb.title}</span>
                      </div>
                      {tb.taskIds.length === 0 ? (
                        <p className="px-6 py-2 text-xs text-neutral-400 italic">(Unassigned)</p>
                      ) : (
                        tb.taskIds.map(tid => {
                          const task = taskLookup[tid];
                          return (
                            <div key={tid} className="flex items-center gap-2 px-6 py-1.5">
                              <span className="text-neutral-400 text-xs">└</span>
                              <span className="text-sm text-neutral-700 flex-1 truncate">
                                {task?.title || tid}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeAssignment(tid)}
                                className="text-neutral-300 hover:text-neutral-500 transition-colors ml-2"
                                aria-label={`Remove ${task?.title || tid}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ))
                )
              )}
            </div>

            {/* footer */}
            <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-safe shrink-0">
              <button
                type="button"
                onClick={onBack}
                disabled={isLoading}
                className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors px-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              {phase === 'recommend' ? (
                availableTasks.length === 0 ? (
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={isLoading}
                    className="btn-secondary btn-sm !py-[7px]"
                  >
                    Skip
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onConfirmSelection(Array.from(selectedIds))}
                    disabled={isLoading || selectedIds.size === 0}
                    className="btn-primary btn-sm"
                  >
                    Assign →
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={onApply}
                  disabled={isLoading}
                  className="btn-primary btn-sm"
                >
                  Apply
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default TaskAssignmentModal;
